import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import type { ComplianceDocumentSectionRecord } from '@artem-lex/compliance-documents'
import { apiClient } from '@/shared/api/apiClient'
import {
  parseFilenameFromContentDisposition,
  triggerBlobDownload,
} from '@/shared/lib/downloadBlob'
import { toCommaQueryParam } from './commaQueryParam'
import {
  isAllowedPackagingItemDocumentFile,
  resolvePackagingItemDocumentContentType,
  type PackagingItemDocumentRequirements,
  type PackagingItemDocumentType,
} from '@/shared/components/packaging/packagingDocumentTypes'
import {
  assertPackagingDocumentFileReadable,
  PackagingDocumentInvalidUploadError,
} from '@/shared/lib/detectUnreadableFileContainer'
import { assertUploadFileSize } from '@/shared/lib/uploadGuardrails'
import { uploadFileToSignedOssUrl } from '@/shared/lib/uploadFileToSignedOssUrl'
import type { PackagingItemChangeHistoryEntry } from '@/shared/packagingItemChangeHistory'

export type ComponentMaterial =
  | 'PLASTIC'
  | 'PAPER_CARDBOARD'
  | 'GLASS'
  | 'METAL_ALUMINIUM'
  | 'METAL_STEEL'
  | 'WOOD'
  | 'OTHER'

export type RecyclingStream =
  | 'GLASS'
  | 'PAPER_CARDBOARD'
  | 'PLASTICS_HDPE'
  | 'PLASTICS_PET'
  | 'PLASTICS_PP'
  | 'PLASTICS_LDPE'
  | 'OTHER_RECYCLABLE_WASTE'
  | 'NONE'

export type PackagingPpwrComplianceLevel =
  | 'NONE'
  | 'SUFFICIENTLY_MINIMIZED'
  | 'NOT_SUFFICIENTLY_MINIMIZED'

export type PackagingActor = 'LEX' | 'SUPPLIER'

export type {
  PackagingItemDocumentRequirements,
  PackagingItemDocumentType,
} from '@/shared/components/packaging/packagingDocumentTypes'

export type PackagingComponent = {
  id?: string
  name: string
  material: ComponentMaterial
  weightInGrams: number
  recyclable: boolean
  recyclingStream?: RecyclingStream | null
  recyclingPct: string
  /** @deprecated Use recyclingPct. Present until the legacy column is dropped. */
  recyclingPercentage?: number
  reusable: boolean
  notes?: string | null
  weightAsPercentage?: number
}

export type PackagingItemDocumentIngestionStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'

export type PackagingItemDocument = {
  id: string
  fileName: string
  filePathInBucket: string
  documentType: PackagingItemDocumentType
  fileSizeKb?: number | null
  contentType?: string | null
  ingestionStatus?: PackagingItemDocumentIngestionStatus | null
  ingestionStatusUpdatedAt?: string | null
  ingestionError?: string | null
  ingestionErrorCode?: string | null
  ingestionStage?: string | null
  ragDocumentId?: string | null
}

export type PackagingItemPackagedItemLink = {
  id: string
  name: string
  sku: string
}

export type PackagingSupplierPackagingItemSummary = {
  id: string
  name: string
  sku: string | null
  completedAt?: string | null
  skuInBomList?: boolean
}

export type PackagingSupplierPackagedItem = {
  id: string
  name: string
  sku: string
  description: string
  notInSourceList?: boolean
  fromPartsCatalog?: boolean
  packagingItems: PackagingSupplierPackagingItemSummary[]
}

export type PackagingItem = {
  id: string
  packagingSupplierId: string
  name: string
  description: string
  intendedUse: string
  sku?: string | null
  packedItemSku?: string | null
  volumeCm3?: string | null
  packagesOwnProduct?: boolean
  supplierIsManufacturer?: boolean | null
  manufacturerName?: string | null
  manufacturerAddress?: string | null
  heavyMetalsStatus?: PackagingPpwrComplianceLevel | null
  substancesOfConcernStatus?: PackagingPpwrComplianceLevel | null
  createdByActor?: PackagingActor | null
  completedByActor?: PackagingActor | null
  totalWeight?: number
  components: PackagingComponent[]
  documents?: PackagingItemDocument[]
  documentRequirements?: PackagingItemDocumentRequirements
  completedAt?: string | null
  packagedItems?: PackagingItemPackagedItemLink[]
  skuInBomList?: boolean
  fieldCrossCheckHistory?: PackagingFieldCrossCheckStoredEntry[]
  heavyMetalsAuditHistory?: PackagingHeavyMetalsAuditStoredEntry[]
  svhcAuditHistory?: PackagingSvhcAuditStoredEntry[]
  recyclabilityAssessmentHistory?: PackagingRecyclabilityAssessmentStoredEntry[]
  minimisationAssessmentHistory?: PackagingMinimisationAssessmentStoredEntry[]
  changeHistory?: PackagingItemChangeHistoryEntry[]
  deletedAt?: string | null
  mergedIntoPackagingItem?: { id: string; name: string } | null
  /** True after at least one EU Declaration of Conformity PDF download. */
  declarationOfConformityDownloaded?: boolean
  outputDocuments?: PackagingItemOutputDocument[]
}

export type PackagingItemOutputDocumentStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'

export type PackagingItemOutputDocument = {
  id: string
  packagingItemId: string
  documentType: string
  declarationNumber: string | null
  fileName: string
  fileSizeKb: number | null
  contentType: string | null
  createdByActor: PackagingActor | null
  createdByUserId: string | null
  createdByUserDisplayName?: string
  status: PackagingItemOutputDocumentStatus
  errorMessage: string | null
  statusUpdatedAt: string | null
  workflowRunId: string | null
  createdAt: string
  updatedAt: string
}

export type PackagingFieldCrossCheckIssueCode =
  | 'NAME_DESCRIPTION_MISMATCH'
  | 'DESCRIPTION_MATERIAL_MISSING_FROM_COMPONENTS'
  | 'COMPONENT_NAME_MATERIAL_MISMATCH'
  | 'MISSING_DIELINE_DIAGRAM'
  | 'MISSING_HEAVY_METALS_TEST_REPORT'
  | 'MISSING_HEAVY_METALS_CONFORMITY_STATEMENT'
  | 'MISSING_SUBSTANCES_OF_CONCERN_TEST_REPORT'
  | 'MISSING_SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT'
  | 'COMPLETED_WITHOUT_COMPONENTS'
  | 'UNREADABLE_OR_PROTECTED_DOCUMENT'
  | 'UNSUPPORTED_DOCUMENT_FILE_TYPE'
  | 'COMPONENT_MATERIAL_RECYCLING_STREAM_MISMATCH'

export type PackagingFieldCrossCheckConfidence = 'HIGH' | 'MEDIUM' | 'LOW'

export type PackagingFieldCrossCheckCheck = {
  code: PackagingFieldCrossCheckIssueCode
  passed: boolean
  reasoningEn: string
  reasoningZh: string
  fields: string[]
  componentIndex: number | null
}

export type PackagingFieldCrossCheckAgentResult = {
  flagged: boolean
  checks: PackagingFieldCrossCheckCheck[]
  confidence: PackagingFieldCrossCheckConfidence
  usage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
  model?: string
}

export type PackagingAuditManualOverrideMeta = {
  fromResult: string
  toResult: string
  reason: string
  overriddenByUserId: string
}

export type PackagingFieldCrossCheckAiStoredEntry =
  PackagingFieldCrossCheckAgentResult & {
    timestamp: string
    runByUserId?: string
  }

export type PackagingFieldCrossCheckManualOverrideStoredEntry = {
  source: 'manual_override'
  flagged: boolean
  timestamp: string
  runByUserId?: string
  manualOverride: PackagingAuditManualOverrideMeta
}

export type PackagingFieldCrossCheckStoredEntry =
  | PackagingFieldCrossCheckManualOverrideStoredEntry
  | PackagingFieldCrossCheckAiStoredEntry

/** Result of a field-cross-check run. A run always produces an AI entry. */
export type PackagingFieldCrossCheckResult = PackagingFieldCrossCheckAiStoredEntry

export type PackagingHeavyMetalsAuditVerdict =
  | 'TEST_REPORT_REDUCED_SUFFICIENTLY'
  | 'TEST_REPORT_PRESENT_NOT_REDUCED_SUFFICIENTLY'
  | 'TEST_REPORT_PRESENT_MATERIAL_MISMATCH'
  | 'TEST_REPORT_PRESENT_DEFICIENT'
  | 'TEST_REPORT_PRESENT_NOT_RELEVANT'
  | 'STATEMENT'
  | 'STATEMENT_PRESENT_NOT_RELEVANT'
  | 'STATEMENT_IS_TEST_REPORT_REDUCED_SUFFICIENTLY'
  | 'STATEMENT_IS_TEST_REPORT_NOT_REDUCED_SUFFICIENTLY'
  | 'STATEMENT_IS_TEST_REPORT_MATERIAL_MISMATCH'
  | 'STATEMENT_IS_TEST_REPORT_NOT_RELEVANT'
  | 'TEST_REPORT_NOT_PRESENT'

export type PackagingHeavyMetalsAuditResult = PackagingHeavyMetalsAuditStoredEntry

export type PackagingHeavyMetalsAuditStoredEntry = {
  verdict: PackagingHeavyMetalsAuditVerdict
  reasoningEn: string
  reasoningZh: string
  evidenceDocumentIds: string[]
  timestamp: string
  runByUserId?: string
  source?: 'manual_override'
  manualOverride?: PackagingAuditManualOverrideMeta
  usage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
  model?: string
}

export type PackagingSvhcAuditVerdict =
  | 'SVHC_TEST_REPORT_SVHC_NEGATIVE'
  | 'SVHC_TEST_REPORT_SVHC_POSITIVE'
  | 'SVHC_TEST_REPORT_INCONCLUSIVE'
  | 'SVHC_TEST_REPORT_NOT_PRESENT'

export type PackagingSvhcAuditResult = PackagingSvhcAuditStoredEntry

export type PackagingSvhcAuditStoredEntry = {
  verdict: PackagingSvhcAuditVerdict
  reasoningEn: string
  reasoningZh: string
  evidenceDocumentIds: string[]
  timestamp: string
  runByUserId?: string
  source?: 'manual_override'
  manualOverride?: PackagingAuditManualOverrideMeta
  usage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
  model?: string
}

export type PackagingAuditKind = 'submission' | 'heavy_metals' | 'svhc'

export type ManualOverridePackagingItemAuditPayload = {
  auditKind: PackagingAuditKind
  newResult: string
  reason: string
}

export type ManualOverridePackagingItemAuditResponse = {
  fieldCrossCheckHistory?: PackagingFieldCrossCheckStoredEntry[]
  heavyMetalsAuditHistory?: PackagingHeavyMetalsAuditStoredEntry[]
  svhcAuditHistory?: PackagingSvhcAuditStoredEntry[]
  changeHistory?: PackagingItemChangeHistoryEntry[]
}

export type RecyclabilityAssessmentFamily =
  | 'paper'
  | 'plastics'
  | 'plasticsMetals'
  | 'unsupported'

export type RecyclabilityClass = 'A' | 'B' | 'C' | 'NOT_APPLICABLE'

export type RecyclabilityStreamClassification =
  | 'PET_BOTTLES'
  | 'PET_THERMOFORM'
  | 'PE_FILM'
  | 'PP_FILM'
  | 'HDPE_CONTAINERS'
  | 'PP_CONTAINERS'
  | 'HDPE_PP_CRATES_PALLETS'
  | 'PS'
  | 'EPS'
  | 'ALUMINIUM'
  | 'STEEL'
  | 'PAPER_CARDBOARD'
  | 'OTHER'
  | 'NOT_APPLICABLE'

export type RecyclabilityConfidence = 'HIGH' | 'MEDIUM' | 'LOW'

export type RecyclabilityPenaltyV1 = {
  type: string
  appliesTo: string
  classShift: string
  note: string
}

export type RecyclabilityPenaltyV2 = {
  type: string
  appliesTo: string
  classShift: string
  noteEn: string
  noteZh: string
}

export type RecyclabilityPenalty = RecyclabilityPenaltyV1 | RecyclabilityPenaltyV2

export type RecyclabilityDisqualifierV1 = {
  criterion: string
  appliesTo: string
  note: string
}

export type RecyclabilityDisqualifierV2 = {
  criterion: string
  appliesTo: string
  noteEn: string
  noteZh: string
}

export type RecyclabilityDisqualifier =
  | RecyclabilityDisqualifierV1
  | RecyclabilityDisqualifierV2

export type PaperRecyclabilityAssessmentResultV1 = {
  methodology: 'EN_643'
  isRecyclable: boolean
  recyclabilityClass: RecyclabilityClass
  targetedRecyclingStream: RecyclabilityStreamClassification
  dominantMaterial: string
  recyclableContentPct: number | null
  weightBasis: string
  baseClass: RecyclabilityClass
  penalties: RecyclabilityPenaltyV1[]
  disqualifier: RecyclabilityDisqualifierV1 | null
  confidence: RecyclabilityConfidence
  dataIssues: string[]
  missingInformation: string[]
  classReasoning: string
  isRecyclableReasoning: string
}

export type PaperRecyclabilityAssessmentResultV2 = {
  methodology: 'EN_643'
  isRecyclable: boolean
  recyclabilityClass: RecyclabilityClass
  targetedRecyclingStream: RecyclabilityStreamClassification
  dominantMaterial: string
  recyclableContentPct: number | null
  weightBasis: string
  baseClass: RecyclabilityClass
  penalties: RecyclabilityPenaltyV2[]
  disqualifier: RecyclabilityDisqualifierV2 | null
  confidence: RecyclabilityConfidence
  dataIssuesEn: string[]
  dataIssuesZh: string[]
  missingInformationEn: string[]
  missingInformationZh: string[]
  classReasoningEn: string
  classReasoningZh: string
  isRecyclableReasoningEn: string
  isRecyclableReasoningZh: string
}

export type PaperRecyclabilityAssessmentResult =
  | PaperRecyclabilityAssessmentResultV1
  | PaperRecyclabilityAssessmentResultV2

export type En18120DfrCategoryAssessmentV1 = {
  categoryName: string
  decision: 'GREEN' | 'YELLOW' | 'RED' | null
  reasoning: string
  matchedRule: string | null
}

export type En18120DfrCategoryAssessmentV2 = {
  categoryName: string
  decision: 'GREEN' | 'YELLOW' | 'RED' | null
  reasoningEn: string
  reasoningZh: string
  matchedRule: string | null
}

export type En18120DfrCategoryAssessment =
  | En18120DfrCategoryAssessmentV1
  | En18120DfrCategoryAssessmentV2

export type En18120DfrAssessmentResultV1 = {
  methodology: 'EN_18120_DFR'
  dominantMaterial: string
  targetedStream: string
  standardRefs: string[]
  tableIds: string[]
  selectionReasoning?: string
  categories: En18120DfrCategoryAssessmentV1[]
  overallCompatible: boolean
  overallReasoning: string
  confidence: RecyclabilityConfidence
  dataIssues: string[]
  missingInformation: string[]
  recyclableContentPct?: number | null
  recyclabilityGrade?: 'A' | 'B' | 'C' | 'TECHNICALLY_NON_RECYCLABLE' | null
  isRecyclable?: boolean
}

export type En18120DfrAssessmentResultV2 = {
  methodology: 'EN_18120_DFR'
  dominantMaterial: string
  targetedStream: string
  standardRefs: string[]
  tableIds: string[]
  selectionReasoningEn?: string
  selectionReasoningZh?: string
  categories: En18120DfrCategoryAssessmentV2[]
  overallCompatible: boolean
  overallReasoningEn: string
  overallReasoningZh: string
  confidence: RecyclabilityConfidence
  dataIssuesEn: string[]
  dataIssuesZh: string[]
  missingInformationEn: string[]
  missingInformationZh: string[]
  recyclableContentPct?: number | null
  recyclabilityGrade?: 'A' | 'B' | 'C' | 'TECHNICALLY_NON_RECYCLABLE' | null
  isRecyclable?: boolean
}

export type En18120DfrAssessmentResult =
  | En18120DfrAssessmentResultV1
  | En18120DfrAssessmentResultV2

export type UnsupportedRecyclabilityAssessmentResultV1 = {
  methodology: 'UNSUPPORTED'
  dominantMaterial: string | null
  reasoning: string
}

export type UnsupportedRecyclabilityAssessmentResultV2 = {
  methodology: 'UNSUPPORTED'
  dominantMaterial: string | null
  reasoningEn: string
  reasoningZh: string
}

export type UnsupportedRecyclabilityAssessmentResult =
  | UnsupportedRecyclabilityAssessmentResultV1
  | UnsupportedRecyclabilityAssessmentResultV2

export type LegacyPackagingRecyclabilityAssessmentResult = {
  assessmentVersion: 'v1'
  assessmentFamily: RecyclabilityAssessmentFamily
  isRecyclable: boolean
  recyclabilityClass: RecyclabilityClass
  targetedRecyclingStream: RecyclabilityStreamClassification
  dominantMaterial: string
  recyclableContentPct: number | null
  weightBasis: string
  baseClass: RecyclabilityClass
  penalties: RecyclabilityPenalty[]
  disqualifier: RecyclabilityDisqualifier | null
  confidence: RecyclabilityConfidence
  dataIssues: string[]
  missingInformation: string[]
  classReasoning: string
  isRecyclableReasoning: string
  usage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
  model?: string
}

type RecyclabilityAssessmentUsage = {
  promptTokens: number
  completionTokens: number
  totalTokens: number
}

export type PaperRecyclabilityAssessmentStoredEntry =
  | {
      kind: 'paper_recyclability'
      assessmentVersion: 'paper-v1'
      timestamp: string
      runByUserId?: string
      usage?: RecyclabilityAssessmentUsage
      model?: string
      result: PaperRecyclabilityAssessmentResultV1
    }
  | {
      kind: 'paper_recyclability'
      assessmentVersion: 'paper-v2'
      timestamp: string
      runByUserId?: string
      usage?: RecyclabilityAssessmentUsage
      model?: string
      result: PaperRecyclabilityAssessmentResultV2
    }

export type En18120DfrAssessmentStoredEntry =
  | {
      kind: 'en18120_dfr'
      assessmentVersion: 'en18120-dfr-v1'
      timestamp: string
      runByUserId?: string
      usage?: RecyclabilityAssessmentUsage
      model?: string
      result: En18120DfrAssessmentResultV1
    }
  | {
      kind: 'en18120_dfr'
      assessmentVersion: 'en18120-dfr-v2'
      timestamp: string
      runByUserId?: string
      usage?: RecyclabilityAssessmentUsage
      model?: string
      result: En18120DfrAssessmentResultV2
    }

export type UnsupportedRecyclabilityAssessmentStoredEntry =
  | {
      kind: 'unsupported'
      assessmentVersion: 'unsupported-v1'
      timestamp: string
      runByUserId?: string
      usage?: RecyclabilityAssessmentUsage
      model?: string
      result: UnsupportedRecyclabilityAssessmentResultV1
    }
  | {
      kind: 'unsupported'
      assessmentVersion: 'unsupported-v2'
      timestamp: string
      runByUserId?: string
      usage?: RecyclabilityAssessmentUsage
      model?: string
      result: UnsupportedRecyclabilityAssessmentResultV2
    }

export type LegacyPackagingRecyclabilityAssessmentStoredEntry =
  LegacyPackagingRecyclabilityAssessmentResult & {
    timestamp: string
    runByUserId?: string
  }

export type PackagingRecyclabilityAssessmentStoredEntry =
  | PaperRecyclabilityAssessmentStoredEntry
  | En18120DfrAssessmentStoredEntry
  | UnsupportedRecyclabilityAssessmentStoredEntry
  | LegacyPackagingRecyclabilityAssessmentStoredEntry

export type PackagingRecyclabilityAssessmentResult =
  PackagingRecyclabilityAssessmentStoredEntry

export type RecyclabilityAssessmentDocumentProseByCriterion = {
  a3_1: { en: string; zh: string }
  b2_1: { en: string; zh: string }
  b4_2: { en: string; zh: string }
  b4_3: { en: string; zh: string }
}

export type RecyclabilityAssessmentDocumentAnnex1Prose = {
  constituentsCollectionSorting: { en: string; zh: string }
  constituentsRecycling: { en: string; zh: string }
  constituentsNegativeInfluence: { en: string; zh: string }
}

export type PackagingRecyclabilityAssessmentDocumentProseResult = {
  prose: RecyclabilityAssessmentDocumentProseByCriterion
  annex1: RecyclabilityAssessmentDocumentAnnex1Prose
}

export type ExportPackagingRecyclabilityAssessmentDocumentPdfPayload = {
  sections: ComplianceDocumentSectionRecord[]
  coverMarkdown?: string
  documentName?: string
}

export type ExportPackagingMinimisationAssessmentDocumentPdfPayload = {
  sections: ComplianceDocumentSectionRecord[]
  coverMarkdown?: string
  documentName?: string
}

export type PackagingMinimisationStandardCriterionV1 = {
  isCriticalArea: boolean
  explanation: string
  followUpQuestion?: string | null
  mostImportantRequirement?: string | null
}

export type PackagingMinimisationStandardCriterionV2 = {
  isCriticalArea: boolean
  explanationEn: string
  explanationZh: string
  followUpQuestionEn?: string | null
  followUpQuestionZh?: string | null
  mostImportantRequirementEn?: string | null
  mostImportantRequirementZh?: string | null
}

export type PackagingMinimisationStandardCriterion =
  | PackagingMinimisationStandardCriterionV1
  | PackagingMinimisationStandardCriterionV2

export type PackagingMinimisationLegislationCriterionV1 = {
  explanation: string
}

export type PackagingMinimisationLegislationCriterionV2 = {
  explanationEn: string
  explanationZh: string
}

export type PackagingMinimisationLegislationCriterion =
  | PackagingMinimisationLegislationCriterionV1
  | PackagingMinimisationLegislationCriterionV2

export type PackagingMinimisationCriteriaResultV1 = {
  productProtection: PackagingMinimisationStandardCriterionV1
  packagingManufacturingProcess: PackagingMinimisationStandardCriterionV1
  packingFillingProcess: PackagingMinimisationStandardCriterionV1
  logistics: PackagingMinimisationStandardCriterionV1
  productPresentationAndMarketing: PackagingMinimisationStandardCriterionV1
  userConsumerAcceptance: PackagingMinimisationStandardCriterionV1
  informationPC: PackagingMinimisationStandardCriterionV1
  safetyPC: PackagingMinimisationStandardCriterionV1
  legislationPC: PackagingMinimisationLegislationCriterionV1
}

export type PackagingMinimisationCriteriaResultV2 = {
  productProtection: PackagingMinimisationStandardCriterionV2
  packagingManufacturingProcess: PackagingMinimisationStandardCriterionV2
  packingFillingProcess: PackagingMinimisationStandardCriterionV2
  logistics: PackagingMinimisationStandardCriterionV2
  productPresentationAndMarketing: PackagingMinimisationStandardCriterionV2
  userConsumerAcceptance: PackagingMinimisationStandardCriterionV2
  informationPC: PackagingMinimisationStandardCriterionV2
  safetyPC: PackagingMinimisationStandardCriterionV2
  legislationPC: PackagingMinimisationLegislationCriterionV2
}

export type PackagingMinimisationCriteriaResult =
  | PackagingMinimisationCriteriaResultV1
  | PackagingMinimisationCriteriaResultV2

export type PackagingMinimisationOutcomeResultV1 = {
  totalMinimumNecessaryWeightGrams: number | null
  totalMinimumNecessaryVolumeCm3: number | null
  reasoning: string
}

export type PackagingMinimisationOutcomeResultV2 = {
  totalMinimumNecessaryWeightGrams: number | null
  totalMinimumNecessaryVolumeCm3: number | null
  reasoningEn: string
  reasoningZh: string
}

export type PackagingMinimisationOutcomeResult =
  | PackagingMinimisationOutcomeResultV1
  | PackagingMinimisationOutcomeResultV2

export type PackagingMinimisationAssessmentStoredEntry =
  | {
      assessmentVersion: 'headless-minimisation-v1'
      criteria: PackagingMinimisationCriteriaResultV1
      outcome: PackagingMinimisationOutcomeResultV1
      timestamp: string
      runByUserId?: string
      usage?: {
        promptTokens: number
        completionTokens: number
        totalTokens: number
      }
      model?: string
    }
  | {
      assessmentVersion: 'headless-minimisation-v2'
      criteria: PackagingMinimisationCriteriaResultV2
      outcome: PackagingMinimisationOutcomeResultV2
      timestamp: string
      runByUserId?: string
      usage?: {
        promptTokens: number
        completionTokens: number
        totalTokens: number
      }
      model?: string
    }

export type PackagingMinimisationAssessmentResult =
  PackagingMinimisationAssessmentStoredEntry

export function isLegacyRecyclabilityAssessmentEntry(
  entry: PackagingRecyclabilityAssessmentStoredEntry
): entry is LegacyPackagingRecyclabilityAssessmentStoredEntry {
  return !('kind' in entry)
}

export function isPaperRecyclabilityAssessmentEntry(
  entry: PackagingRecyclabilityAssessmentStoredEntry
): entry is PaperRecyclabilityAssessmentStoredEntry {
  return 'kind' in entry && entry.kind === 'paper_recyclability'
}

export function isEn18120DfrAssessmentEntry(
  entry: PackagingRecyclabilityAssessmentStoredEntry
): entry is En18120DfrAssessmentStoredEntry {
  return 'kind' in entry && entry.kind === 'en18120_dfr'
}

export function isUnsupportedRecyclabilityAssessmentEntry(
  entry: PackagingRecyclabilityAssessmentStoredEntry
): entry is UnsupportedRecyclabilityAssessmentStoredEntry {
  return 'kind' in entry && entry.kind === 'unsupported'
}

export type PackagingSupplierOnboardingStatus =
  | 'INVITE_SENT'
  | 'INVITE_OPENED'
  | 'ACKNOWLEDGED'

export type PackagingSupplierComplianceProgress =
  | 'NO_ITEMS'
  | 'DRAFT_ITEMS'
  | 'ALL_ITEMS_COMPLETE'

export type PackagingSupplierRedFlag =
  | 'HEAVY_METALS'
  | 'SUBSTANCES_OF_CONCERN'
  | 'INCOMPLETE_INFORMATION'

export type PackagingSupplier = {
  id: string
  organizationId: string
  name: string
  address?: string | null
  contactName?: string | null
  contactEmail?: string | null
  magicLinkToken: string
  magicLinkUrl: string
  ppwrComplianceAcknowledgedAt?: string | null
  ppwrComplianceAcknowledgedByName?: string | null
  magicLinkFirstOpenedAt?: string | null
  onboardingStatus?: PackagingSupplierOnboardingStatus
  complianceProgress?: PackagingSupplierComplianceProgress
  allPackedItemsComplete?: boolean
  completedPackagingItemCount?: number
  totalPackagingItemCount?: number
  packedItemCount?: number
  redFlags?: PackagingFieldCrossCheckIssueCode[]
  packedItemNames?: string[]
  packedItemSkus?: string[]
  packagingItemNames?: string[]
  packagingItemSkus?: string[]
  packagingItems?: PackagingItem[]
  packagedItems?: PackagingSupplierPackagedItem[]
  notInSourceList?: boolean
}

export type PackagingSuppliersListResponse = {
  partsCatalogConfigured: boolean
  suppliers: PackagingSupplier[]
  pageInfo: {
    page: number
    pageSize: number
    totalCount: number
    totalPages: number
  }
}

export type PackagingSuppliersListQuery = {
  page?: number
  pageSize?: number
  searchQuery?: string
  funnel?: string[]
  redFlags?: PackagingFieldCrossCheckIssueCode[]
  notInSourceList?: boolean
  sortBy?: PackagingSuppliersOverviewSortBy
  sortDir?: PackagingSuppliersOverviewSortDir
}

export type PackagingSuppliersOverviewSortBy =
  | 'name'
  | 'packedItemCount'
  | 'funnelStatus'
  | 'redFlags'

export type PackagingSuppliersOverviewSortDir = 'asc' | 'desc'

export type PackagingSuppliersExportQuery = Omit<
  PackagingSuppliersListQuery,
  'page' | 'pageSize'
>

export type PackagingSuppliersExportResponse = {
  suppliers: PackagingSupplier[]
}

export type PackagingSuppliersStats = {
  totalSuppliers: number
  opened: number
  openedPercent: number
  acknowledged: number
  acknowledgedPercent: number
  withPackagingItems: number
  withPackagingItemsPercent: number
  allItemsComplete: number
  allItemsCompletePercent: number
}

export type PackagingItemsStats = {
  total: number
  inDraft: number
  complete: number
  completePercent: number
  withRedFlags: number
}

export type CreatePackagingSupplierPayload = {
  name: string
}

export type UpdatePackagingSupplierPayload = {
  name?: string
  address?: string | null
  contactName?: string | null
  contactEmail?: string | null
}

export type PackagingComponentInput = Omit<
  PackagingComponent,
  'id' | 'weightAsPercentage' | 'recyclingPercentage'
>

export type PackagingItemPayload = {
  name: string
  description: string
  intendedUse: string
  sku?: string | null
  packedItemSku?: string | null
  volumeCm3?: string | null
  packagesOwnProduct: boolean
  supplierIsManufacturer: boolean
  manufacturerName?: string
  manufacturerAddress?: string
  heavyMetalsStatus: PackagingPpwrComplianceLevel
  substancesOfConcernStatus: PackagingPpwrComplianceLevel
  components?: PackagingComponentInput[]
  packagedItemIds?: string[]
}

export type PackagingItemUpdatePayload = Partial<PackagingItemPayload> & {
  completed?: boolean
}

export const PACKAGING_SUPPLIERS_KEY = 'packagingSuppliers'

export type PackagingItemMergedSource = {
  id: string
  name: string
  sku: string | null
  deletedAt: string | null
}

export function packagingItemMergedSourcesQueryKey(
  supplierId: string,
  itemId: string
) {
  return [
    PACKAGING_SUPPLIERS_KEY,
    'items',
    supplierId,
    itemId,
    'merged-sources',
  ] as const
}

export function packagingItemQueryKey(supplierId: string, itemId: string) {
  return [PACKAGING_SUPPLIERS_KEY, 'items', supplierId, itemId] as const
}

export function packagingItemDocumentsQueryKey(
  supplierId: string,
  itemId: string
) {
  return [
    PACKAGING_SUPPLIERS_KEY,
    'items',
    supplierId,
    itemId,
    'documents',
  ] as const
}

export type PackagingSupplierDocumentLibraryRow = {
  id: string
  fileName: string
  documentType: PackagingItemDocumentType
  packagingItemCount: number
  linkedItems: Array<{ id: string; name: string }>
  ingestionStatus: string | null
  fileSizeKb: number | null
  createdAt: string
}

export function packagingSupplierDocumentsQueryKey(supplierId: string) {
  return [PACKAGING_SUPPLIERS_KEY, supplierId, 'supplier-documents'] as const
}

export function useListPackagingSupplierDocuments(supplierId: string) {
  return useQuery<PackagingSupplierDocumentLibraryRow[], Error>({
    queryKey: packagingSupplierDocumentsQueryKey(supplierId),
    queryFn: async () => {
      const response = await apiClient.get<PackagingSupplierDocumentLibraryRow[]>(
        `/v1/packaging-suppliers/${supplierId}/documents`
      )
      return response.data
    },
    enabled: supplierId.length > 0,
  })
}

export function useListPackagingSuppliers(
  query: PackagingSuppliersListQuery = {},
  enabled = true
) {
  return useQuery<PackagingSuppliersListResponse, Error>({
    queryKey: [PACKAGING_SUPPLIERS_KEY, 'list', query],
    queryFn: async () => {
      const response = await apiClient.get<PackagingSuppliersListResponse>(
        '/v1/packaging-suppliers',
        {
          params: {
            page: query.page,
            pageSize: query.pageSize,
            q: query.searchQuery,
            funnel: toCommaQueryParam(query.funnel),
            redFlags: toCommaQueryParam(query.redFlags),
            notInSourceList:
              query.notInSourceList === true ? 'true' : undefined,
            sortBy: query.sortBy,
            sortDir: query.sortDir,
          },
        }
      )
      return response.data
    },
    enabled,
    placeholderData: keepPreviousData,
  })
}

export async function downloadPackagingSuppliersExport(
  query: PackagingSuppliersExportQuery = {},
  fallbackFilename: string
): Promise<void> {
  const response = await apiClient.get<Blob>('/v1/packaging-suppliers/export', {
    params: {
      q: query.searchQuery,
      funnel: toCommaQueryParam(query.funnel),
      redFlags: toCommaQueryParam(query.redFlags),
      notInSourceList: query.notInSourceList === true ? 'true' : undefined,
      sortBy: query.sortBy,
      sortDir: query.sortDir,
    },
    responseType: 'blob',
    timeout: 120_000,
  })
  const filename = parseFilenameFromContentDisposition(
    response.headers['content-disposition'],
    fallbackFilename
  )
  triggerBlobDownload(response.data, filename)
}

export function useListPackagingSuppliersStats(enabled = true) {
  return useQuery<PackagingSuppliersStats, Error>({
    queryKey: [PACKAGING_SUPPLIERS_KEY, 'suppliers-stats'],
    queryFn: async () => {
      const response = await apiClient.get<PackagingSuppliersStats>(
        '/v1/packaging-suppliers/stats'
      )
      return response.data
    },
    enabled,
  })
}

export function useListPackagingItemsStats(enabled = true) {
  return useQuery<PackagingItemsStats, Error>({
    queryKey: [PACKAGING_SUPPLIERS_KEY, 'packaging-items-stats'],
    queryFn: async () => {
      const response = await apiClient.get<PackagingItemsStats>(
        '/v1/packaging-suppliers/packaging-items-stats'
      )
      return response.data
    },
    enabled,
  })
}

export function useGetPackagingSupplier(id: string | undefined) {
  return useQuery<PackagingSupplier, Error>({
    queryKey: [PACKAGING_SUPPLIERS_KEY, 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<PackagingSupplier>(
        `/v1/packaging-suppliers/${id}`
      )
      return response.data
    },
    enabled: Boolean(id),
  })
}

export function useCreatePackagingSupplier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreatePackagingSupplierPayload) => {
      const response = await apiClient.post<PackagingSupplier>(
        '/v1/packaging-suppliers',
        payload
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export function useUpdatePackagingSupplier(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdatePackagingSupplierPayload) => {
      const response = await apiClient.patch<PackagingSupplier>(
        `/v1/packaging-suppliers/${id}`,
        payload
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export function useDeletePackagingSupplier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/v1/packaging-suppliers/${id}`)
    },
    onSuccess: (_data, id) => {
      queryClient.removeQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY, 'detail', id],
      })
      queryClient.removeQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY, 'items', id],
      })
      void queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY, 'list'],
      })
    },
  })
}

export function useRotatePackagingSupplierMagicLink(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<PackagingSupplier>(
        `/v1/packaging-suppliers/${id}/rotate-magic-link`
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export function useCreatePackagingItem(supplierId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PackagingItemPayload) => {
      const response = await apiClient.post<PackagingItem>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items`,
        payload
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export function useDeletePackagingItem(supplierId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (itemId: string) => {
      await apiClient.delete(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}`
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export function useGetPackagingItem(
  supplierId: string | undefined,
  itemId: string | undefined,
  options?: {
    refetchInterval?:
      | number
      | false
      | ((query: {
          state: { data?: PackagingItem }
        }) => number | false | undefined)
  }
) {
  return useQuery<PackagingItem, Error>({
    queryKey: packagingItemQueryKey(supplierId ?? '', itemId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<PackagingItem>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}`
      )
      return response.data
    },
    enabled: Boolean(supplierId && itemId),
    refetchInterval: options?.refetchInterval,
  })
}

export function useGetPackagingItemMergedSources(
  supplierId: string | undefined,
  itemId: string | undefined,
  options?: { enabled?: boolean }
) {
  const queryEnabled =
    Boolean(supplierId && itemId) && (options?.enabled ?? true)
  return useQuery<{ items: PackagingItemMergedSource[] }, Error>({
    queryKey: packagingItemMergedSourcesQueryKey(
      supplierId ?? '',
      itemId ?? ''
    ),
    queryFn: async () => {
      const response = await apiClient.get<{
        items: PackagingItemMergedSource[]
      }>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/merged-sources`
      )
      return response.data
    },
    enabled: queryEnabled,
  })
}

export function useUpdatePackagingItem(supplierId: string, itemId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PackagingItemUpdatePayload) => {
      const response = await apiClient.patch<PackagingItem>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}`,
        payload
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
    },
  })
}

export function useUnlinkPackagingItemFromPackedItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      supplierId,
      packagingItemId,
      packedItemId,
    }: {
      supplierId: string
      packagingItemId: string
      packedItemId: string
    }) => {
      const response = await apiClient.get<PackagingItem>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${packagingItemId}`
      )
      const remainingIds = (response.data.packagedItems ?? [])
        .map((part) => part.id)
        .filter((id) => id !== packedItemId)
      let payload: PackagingItemUpdatePayload = {
        packagedItemIds: remainingIds,
      }
      if (remainingIds.length === 0) {
        payload = {
          packagesOwnProduct: false,
          packagedItemIds: [],
        }
      }
      const updated = await apiClient.patch<PackagingItem>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${packagingItemId}`,
        payload
      )
      return updated.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export function useRunPackagingItemFieldCrossCheck(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<PackagingFieldCrossCheckResult>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/field-cross-check`
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
    },
  })
}

const HEAVY_METALS_AUDIT_REQUEST_TIMEOUT_MS = 180_000

export function useRunPackagingItemHeavyMetalsAudit(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<PackagingHeavyMetalsAuditResult>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/heavy-metals-audit`,
        undefined,
        { timeout: HEAVY_METALS_AUDIT_REQUEST_TIMEOUT_MS }
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY, 'packaging-items-overview'],
      })
    },
  })
}

const SVHC_AUDIT_REQUEST_TIMEOUT_MS = 180_000

export function useRunPackagingItemSvhcAudit(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<PackagingSvhcAuditResult>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/svhc-audit`,
        undefined,
        { timeout: SVHC_AUDIT_REQUEST_TIMEOUT_MS }
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY, 'packaging-items-overview'],
      })
    },
  })
}

export function useManualOverridePackagingItemAudit(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ManualOverridePackagingItemAuditPayload) => {
      const response = await apiClient.post<ManualOverridePackagingItemAuditResponse>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/audit-manual-override`,
        payload
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY, 'packaging-items-overview'],
      })
    },
  })
}

/** EN 18120 runs table selection + category assessment sequentially. */
const RECYCLABILITY_ASSESSMENT_REQUEST_TIMEOUT_MS = 180_000

const RECYCLABILITY_DOCUMENT_PROSE_REQUEST_TIMEOUT_MS = 180_000

export function useGeneratePackagingRecyclabilityAssessmentDocumentProse(
  supplierId: string,
  itemId: string
) {
  return useMutation({
    mutationFn: async () => {
      const response =
        await apiClient.post<PackagingRecyclabilityAssessmentDocumentProseResult>(
          `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/recyclability-assessment-document-prose`,
          undefined,
          { timeout: RECYCLABILITY_DOCUMENT_PROSE_REQUEST_TIMEOUT_MS }
        )
      return response.data
    },
  })
}

const RECYCLABILITY_DOCUMENT_EXPORT_PDF_TIMEOUT_MS = 180_000

export function useExportPackagingRecyclabilityAssessmentDocumentPdf(
  supplierId: string,
  itemId: string
) {
  return useMutation<void, Error, ExportPackagingRecyclabilityAssessmentDocumentPdfPayload>(
    {
      mutationFn: async (payload) => {
        const baseURL = apiClient.defaults.baseURL ?? ''
        const url = `${baseURL}/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/recyclability-assessment-document/export-pdf`
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: AbortSignal.timeout(
            RECYCLABILITY_DOCUMENT_EXPORT_PDF_TIMEOUT_MS
          ),
        })
        if (!response.ok) {
          throw new Error(`Export failed: ${response.statusText}`)
        }
        const blob = await response.blob()
        const downloadUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download =
          payload.documentName ?? 'recyclability-assessment.pdf'
        link.click()
        URL.revokeObjectURL(downloadUrl)
      },
    }
  )
}

const MINIMISATION_DOCUMENT_EXPORT_PDF_TIMEOUT_MS = 180_000

export function useExportPackagingMinimisationAssessmentDocumentPdf(
  supplierId: string,
  itemId: string
) {
  return useMutation<
    void,
    Error,
    ExportPackagingMinimisationAssessmentDocumentPdfPayload
  >({
    mutationFn: async (payload) => {
      const baseURL = apiClient.defaults.baseURL ?? ''
      const url = `${baseURL}/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/minimisation-assessment-document/export-pdf`
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(
          MINIMISATION_DOCUMENT_EXPORT_PDF_TIMEOUT_MS
        ),
      })
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`)
      }
      const blob = await response.blob()
      const downloadUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download =
        payload.documentName ?? 'packaging-minimisation-assessment.pdf'
      link.click()
      URL.revokeObjectURL(downloadUrl)
    },
  })
}

const PACKAGING_ITEM_DECLARATION_OF_CONFORMITY_GENERATE_TIMEOUT_MS = 180_000

export const PACKAGING_ITEM_DECLARATION_OF_CONFORMITY_PDF_WORKFLOW_TYPE =
  'packaging_item_declaration_of_conformity_pdf' as const

export type PackagingItemDeclarationOfConformityStreamEvent =
  | { type: 'progress'; message: string }
  | { type: 'workflow.started'; workflowRunId: string; workflowType: string }
  | { type: 'step.started'; step: string }
  | { type: 'step.completed'; step: string }
  | {
      type: 'workflow.completed'
      result?: {
        outputDocumentId: string
        declarationNumber: string
        fileName: string
      }
    }
  | { type: 'error'; error: string }
  | { type: 'workflow.failed'; error?: string }

function getPackagingStreamUrl(path: string): string {
  const baseURL = apiClient.defaults.baseURL ?? ''
  return `${baseURL}${path}`
}

function getPackagingStreamHeaders(): Record<string, string> {
  const common = (apiClient.defaults.headers.common ?? {}) as Record<
    string,
    string
  >
  const { 'Content-Type': _contentType, ...rest } = common
  return rest
}

async function downloadPackagingItemOutputDocumentFromUrl(
  downloadUrl: string,
  fileName: string
) {
  const response = await fetch(downloadUrl)
  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`)
  }
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  link.click()
  URL.revokeObjectURL(objectUrl)
}

export function useDownloadPackagingItemOutputDocument(
  supplierId: string,
  itemId: string
) {
  return useMutation<
    void,
    Error,
    { outputDocumentId: string; fileName?: string }
  >({
    mutationFn: async ({ outputDocumentId, fileName }) => {
      const response = await apiClient.get<{
        downloadUrl: string
        fileName: string
        declarationNumber: string | null
      }>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/output-documents/${outputDocumentId}/download-url`
      )
      await downloadPackagingItemOutputDocumentFromUrl(
        response.data.downloadUrl,
        fileName ?? response.data.fileName
      )
    },
  })
}

export function useStartPackagingItemDeclarationOfConformityGeneration(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()

  const invalidateItem = () => {
    void queryClient.invalidateQueries({
      queryKey: packagingItemQueryKey(supplierId, itemId),
    })
  }

  return useMutation<
    void,
    Error,
    {
      onEvent?: (event: PackagingItemDeclarationOfConformityStreamEvent) => void
      onComplete?: (result: {
        outputDocumentId: string
        declarationNumber: string
        fileName: string
      }) => void
    }
  >({
    mutationFn: async ({ onEvent, onComplete }) => {
      const url = getPackagingStreamUrl(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/declaration-of-conformity/generate`
      )
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getPackagingStreamHeaders(),
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        signal: AbortSignal.timeout(
          PACKAGING_ITEM_DECLARATION_OF_CONFORMITY_GENERATE_TIMEOUT_MS
        ),
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start Declaration of Conformity generation: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }

      // Pending output-document row is created before the SSE body starts.
      invalidateItem()

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let hasInvalidatedOnProgress = false

      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(
              data
            ) as PackagingItemDeclarationOfConformityStreamEvent & {
              type: string
            }
            if (
              !hasInvalidatedOnProgress &&
              (event.type === 'workflow.started' ||
                event.type === 'step.started' ||
                event.type === 'progress')
            ) {
              hasInvalidatedOnProgress = true
              invalidateItem()
            }
            if (
              event.type === 'progress' ||
              event.type === 'step.started' ||
              event.type === 'step.completed' ||
              event.type === 'error'
            ) {
              onEvent?.(event)
            }
            if (event.type === 'workflow.completed') {
              const completedEvent = event as Extract<
                PackagingItemDeclarationOfConformityStreamEvent,
                { type: 'workflow.completed' }
              >
              if (completedEvent.result) {
                onComplete?.(completedEvent.result)
              }
              return
            }
            if (event.type === 'error') {
              throw new Error(event.error ?? 'Generation failed')
            }
            if (event.type === 'workflow.failed') {
              throw new Error(
                (event as { error?: string }).error ?? 'Workflow failed'
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      invalidateItem()
    },
    onError: () => {
      invalidateItem()
    },
  })
}

function parseContentDispositionFilename(
  contentDisposition: string | null
): string | null {
  if (!contentDisposition) {
    return null
  }
  const match = /filename="([^"]+)"/.exec(contentDisposition)
  if (match?.[1]) {
    return match[1]
  }
  return null
}


export function useExportPackagingItemDeclarationOfConformityPdf(
  supplierId: string,
  itemId: string
) {
  const downloadMutation = useDownloadPackagingItemOutputDocument(
    supplierId,
    itemId
  )
  const generateMutation = useStartPackagingItemDeclarationOfConformityGeneration(
    supplierId,
    itemId
  )

  return useMutation<
    void,
    Error,
    {
      onProgress?: (message: string) => void
    } | void
  >({
    mutationFn: async (options) => {
      const onProgress = options?.onProgress
      await generateMutation.mutateAsync({
        onEvent: (event) => {
          if (event.type === 'progress') {
            onProgress?.(event.message)
          }
        },
        onComplete: async (result) => {
          await downloadMutation.mutateAsync({
            outputDocumentId: result.outputDocumentId,
            fileName: result.fileName,
          })
        },
      })
    },
  })
}

export type PackagingConformityStatementLocale = 'en-GB' | 'zh-CN'

const PACKAGING_CONFORMITY_STATEMENT_PDF_TIMEOUT_MS = 180_000

async function downloadPackagingConformityStatementPdf(
  supplierId: string,
  itemId: string,
  pathSuffix: string,
  fallbackFilename: string,
  lng: PackagingConformityStatementLocale
) {
  const baseURL = apiClient.defaults.baseURL ?? ''
  const url = `${baseURL}/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/${pathSuffix}?lng=${encodeURIComponent(lng)}`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    signal: AbortSignal.timeout(PACKAGING_CONFORMITY_STATEMENT_PDF_TIMEOUT_MS),
  })
  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`)
  }
  const blob = await response.blob()
  const filename =
    parseContentDispositionFilename(
      response.headers.get('Content-Disposition')
    ) ?? fallbackFilename
  const downloadUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  link.click()
  URL.revokeObjectURL(downloadUrl)
}

export function useDownloadPackagingHeavyMetalsConformityStatement(
  supplierId: string,
  itemId: string
) {
  return useMutation<void, Error, PackagingConformityStatementLocale>({
    mutationFn: async (lng) => {
      await downloadPackagingConformityStatementPdf(
        supplierId,
        itemId,
        'heavy-metals-conformity-statement.pdf',
        'heavy-metals-conformity-statement.pdf',
        lng
      )
    },
  })
}

export function useDownloadPackagingSubstancesOfConcernConformityStatement(
  supplierId: string,
  itemId: string
) {
  return useMutation<void, Error, PackagingConformityStatementLocale>({
    mutationFn: async (lng) => {
      await downloadPackagingConformityStatementPdf(
        supplierId,
        itemId,
        'substances-of-concern-conformity-statement.pdf',
        'substances-of-concern-conformity-statement.pdf',
        lng
      )
    },
  })
}

export function useRunPackagingItemRecyclabilityAssessment(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response =
        await apiClient.post<PackagingRecyclabilityAssessmentResult>(
          `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/recyclability-assessment`,
          undefined,
          { timeout: RECYCLABILITY_ASSESSMENT_REQUEST_TIMEOUT_MS }
        )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
    },
  })
}

/** Nine parallel criterion agents plus outcome (EN 13428 / PPWR 2.2–2.3.9). */
const MINIMISATION_ASSESSMENT_REQUEST_TIMEOUT_MS = 600_000

export function useRunPackagingItemMinimisationAssessment(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response =
        await apiClient.post<PackagingMinimisationAssessmentResult>(
          `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/minimisation-assessment`,
          undefined,
          { timeout: MINIMISATION_ASSESSMENT_REQUEST_TIMEOUT_MS }
        )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
    },
  })
}

export function usePackagingItemDocuments(
  supplierId: string,
  itemId: string,
  options?: { enabled?: boolean; pollIngestion?: boolean }
) {
  return useQuery<PackagingItemDocument[], Error>({
    queryKey: packagingItemDocumentsQueryKey(supplierId, itemId),
    queryFn: async () => {
      const response = await apiClient.get<PackagingItemDocument[]>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/documents`
      )
      return response.data
    },
    enabled: (options?.enabled ?? true) && Boolean(supplierId && itemId),
    refetchInterval: (query) => {
      if (!options?.pollIngestion) {
        return false
      }
      const documents = query.state.data ?? []
      const needsPoll = documents.some(
        (document) =>
          document.ingestionStatus === 'PROCESSING' ||
          document.ingestionStatus === 'PENDING'
      )
      return needsPoll ? 4000 : false
    },
  })
}

export function useIngestAllPackagingItemDocuments(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{
        startedDocumentIds: string[]
        skippedProcessingDocumentIds: string[]
        skippedCompletedDocumentIds: string[]
        skippedLinkedDocumentIds: string[]
      }>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/documents/ingest`
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemDocumentsQueryKey(supplierId, itemId),
      })
    },
  })
}

export function useIngestPackagingItemDocument(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (documentId: string) => {
      const response = await apiClient.post<{ startedDocumentId: string }>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/documents/${documentId}/ingest`
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemDocumentsQueryKey(supplierId, itemId),
      })
    },
  })
}

export function useRetryPackagingItemDocumentIngestion(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (documentId: string) => {
      const response = await apiClient.post<{ accepted: boolean }>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/documents/${documentId}/retry-ingestion`
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemDocumentsQueryKey(supplierId, itemId),
      })
    },
  })
}

type CreatePackagingDocumentResponse = {
  document: PackagingItemDocument
  uploadUrl: string
  filePathInBucket: string
}

export async function uploadPackagingItemDocumentFile(
  supplierId: string,
  itemId: string,
  file: File,
  documentType: PackagingItemDocumentType
): Promise<PackagingItemDocument> {
  if (file.size === 0) {
    throw new PackagingDocumentInvalidUploadError('empty')
  }
  if (!isAllowedPackagingItemDocumentFile(file)) {
    throw new PackagingDocumentInvalidUploadError('unsupported-type')
  }
  assertUploadFileSize(file)
  await assertPackagingDocumentFileReadable(file)
  const contentType = resolvePackagingItemDocumentContentType(file)
  const fileSizeKb = Math.ceil(file.size / 1024)
  const createResponse = await apiClient.post<CreatePackagingDocumentResponse>(
    `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/documents`,
    {
      fileName: file.name,
      documentType,
      contentType,
      fileSizeKb,
    }
  )
  const { uploadUrl, filePathInBucket, document } = createResponse.data
  const documentsPath = `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/documents/${document.id}`
  try {
    await uploadFileToSignedOssUrl(uploadUrl, file, {
      contentType,
      context: {
        feature: 'packaging-item-document',
        fileName: file.name,
        fileSizeBytes: file.size,
        contentType,
        documentId: document.id,
      },
    })
    const completeResponse = await apiClient.patch<PackagingItemDocument>(
      documentsPath,
      { filePathInBucket }
    )
    return completeResponse.data
  } catch (error) {
    try {
      await apiClient.delete(documentsPath)
    } catch {
      // Best-effort cleanup of placeholder row when upload or complete fails.
    }
    throw error
  }
}

export function useUploadPackagingItemDocument(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      file: File
      documentType: PackagingItemDocumentType
    }) => {
      return uploadPackagingItemDocumentFile(
        supplierId,
        itemId,
        input.file,
        input.documentType
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemDocumentsQueryKey(supplierId, itemId),
      })
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export function useAttachPackagingItemDocument(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      packagingSupplierDocumentId: string
      documentType: PackagingItemDocumentType
    }) => {
      const response = await apiClient.post<PackagingItemDocument>(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/documents/attach`,
        input
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemDocumentsQueryKey(supplierId, itemId),
      })
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
      await queryClient.invalidateQueries({
        queryKey: packagingSupplierDocumentsQueryKey(supplierId),
      })
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export function useDeletePackagingItemDocument(
  supplierId: string,
  itemId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (documentId: string) => {
      await apiClient.delete(
        `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/documents/${documentId}`
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: packagingItemDocumentsQueryKey(supplierId, itemId),
      })
      await queryClient.invalidateQueries({
        queryKey: packagingItemQueryKey(supplierId, itemId),
      })
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export async function fetchPackagingDocumentDownloadUrl(
  supplierId: string,
  itemId: string,
  documentId: string
): Promise<string> {
  const response = await apiClient.get<{ downloadUrl: string }>(
    `/v1/packaging-suppliers/${supplierId}/packaging-items/${itemId}/documents/${documentId}/download-url`
  )
  return response.data.downloadUrl
}

export function usePackagingDocumentDownloadUrl(
  supplierId: string,
  itemId: string
) {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const downloadUrl = await fetchPackagingDocumentDownloadUrl(
        supplierId,
        itemId,
        documentId
      )
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    },
  })
}
